import { Dispatch } from 'redux';
import types from 'redux/actions/actionTypes';
import { axiosV2 } from 'interceptors/axios';

import {
  TradingOfferOperation,
  TradingOfferOperationDocumentRequest,
} from 'types/TradingOfferOperation';
import { PublisherAuditClientContext } from 'modules/trading/utils/collectPublisherAuditContext';

import { decryptResponse } from 'utils/token';
import { dispatchError } from 'utils/dispatchError';
import { ApiQueryParams } from 'types/ApiQueryParams';

type TradingOfferOperationApiGetAllResponse = {
  data: TradingOfferOperation[];
  total: number;
};

type BuyerUpdateData = {
  name?: string;
  cif?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    zip?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  bankAccountNumber?: string;
};

type CreateTradingOfferOperationPayload = Partial<TradingOfferOperation> & {
  participantAuditContext?: PublisherAuditClientContext;
  buyerUpdateData?: BuyerUpdateData;
  domain?: string;
};

export const getTradingOfferOperations =
  (params: { tradingOfferId?: string; societyId?: string } & ApiQueryParams) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get('/tradingOfferOperations', {
        params,
      });

      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferOperationApiGetAllResponse;

      dispatch({
        type: types.GET_TRADING_OFFER_OPERATIONS,
        tradingOfferOperations: decodedData.data,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const getTradingOfferOperation =
  (operationId: string, params?: ApiQueryParams) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get(
        `/tradingOfferOperations/${operationId}`,
        { params }
      );
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferOperation;

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const createTradingOfferOperation =
  (operationData: CreateTradingOfferOperationPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.post(
        '/tradingOfferOperations',
        operationData
      );
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferOperation;

      dispatch({
        type: types.CREATE_TRADING_OFFER_OPERATION,
        tradingOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const updateTradingOfferOperation =
  (operationId: string, operationData: Partial<TradingOfferOperation>) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.put(
        `/tradingOfferOperations/${operationId}`,
        operationData
      );
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferOperation;

      dispatch({
        type: types.UPDATE_TRADING_OFFER_OPERATION,
        tradingOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const requestTradingOfferOperationDocuments =
  (
    operationId: string,
    body: Pick<
      TradingOfferOperationDocumentRequest,
      'documentType' | 'description' | 'deadlineBusinessDays' | 'targetRole'
    >
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.post(
        `/tradingOfferOperations/${operationId}/request-documents`,
        body
      );
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferOperation;

      dispatch({
        type: types.UPDATE_TRADING_OFFER_OPERATION,
        tradingOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const postTradingOfferOperationComment =
  (
    operationId: string,
    body: { text: string; asPartnerId?: string }
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.post(
        `/tradingOfferOperations/${operationId}/comments`,
        body
      );
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferOperation;

      dispatch({
        type: types.UPDATE_TRADING_OFFER_OPERATION,
        tradingOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const deleteTradingOfferOperationComment =
  (operationId: string, commentId: string) => async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.delete(
        `/tradingOfferOperations/${operationId}/comments/${commentId}`
      );
      const decodedData = (await decryptResponse(
        data
      )) as TradingOfferOperation;

      dispatch({
        type: types.UPDATE_TRADING_OFFER_OPERATION,
        tradingOfferOperation: decodedData,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const deleteTradingOfferOperation =
  (operationId: string) => async (dispatch: Dispatch) => {
    try {
      await axiosV2.delete(`/tradingOfferOperations/${operationId}`);

      dispatch({
        type: types.DELETE_TRADING_OFFER_OPERATION,
        tradingOfferOperationId: operationId,
      });
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };
